import { useEffect } from 'react';
import { generateHashEmail } from '../generateHashEmail.ts';
import { isProduction } from '../isProduction.ts';
import pendo from './script.js';

/**
 * Pendo.io is a product analytics and user experience platform designed to help companies
 * better understand how users interact with their software products.
 * You can find more here {@link https://openxtechinc.atlassian.net/wiki/spaces/TECH/pages/3137634413/Pendo+user+task+tracking+tool}
 */
export const initializePendo = (apiKey: string | undefined) => {
  if (apiKey) pendo(apiKey);
};

type UsePendoProps = {
  apiKey?: string;
  email: string;
  organizationType?: string | null;
};

/**
 * Matches normalized email alias and domain.
 * i.e. "user+tag@openx.com" -> [_, "user", "openx.com"]
 */
const NORMALIZED_EMAIL_REGEX = /^([^+@]+)(?:\+[^@]*)?@(.+)$/;
export const PENDO_SALT = 'e9499b3b8461625dbb039ac2e498e0c0';

export function getNormalizedEmailAndDomain(email: string): string[] {
  const match = email.match(NORMALIZED_EMAIL_REGEX);

  if (match) {
    const [alias = 'unknown', domain = 'unknown'] = match.slice(1);
    const normalizedEmail = `${alias}@${domain}`;

    return [normalizedEmail, domain];
  }

  const domain = email.split('@')[1] ?? 'unknown';

  return [email, domain];
}

export const initializeUser = ({ email, apiKey, organizationType }: UsePendoProps) => {
  if (window.pendo && !window.pendo?.visitorId) {
    const deviceWidth = window.screen?.width ?? 0;
    const deviceHeight = window.screen?.height ?? 0;

    const [visitorEmail, domain] = getNormalizedEmailAndDomain(email);

    const hashedEmail = generateHashEmail(visitorEmail, PENDO_SALT);
    const hashedFullEmail = generateHashEmail(email, PENDO_SALT);

    window.pendo.initialize({
      account: {
        id: domain,
        ...(organizationType != null && organizationType !== '' && { organizationType }),
      },
      apiKey,
      visitor: {
        id: hashedEmail,
        domain: domain,
        device_resolution: `${deviceWidth}x${deviceHeight}`,
        device_width: deviceWidth,
        device_height: deviceHeight,
        email: hashedEmail,
        email_full: hashedFullEmail,
        viewport_width: window.innerWidth ?? 0,
        viewport_height: window.innerHeight ?? 0,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      },
    });
  }
};

export const usePendo = ({ apiKey, email, organizationType }: UsePendoProps) => {
  const isLocalDevelopment = window.location.hostname === 'localhost';
  const isPendoEnabled = isProduction() && !isLocalDevelopment;

  const shouldInitialize = isPendoEnabled && email && apiKey;

  useEffect(() => {
    if (shouldInitialize) {
      initializeUser({ apiKey, email, organizationType });
    }
  }, [apiKey, email, shouldInitialize, organizationType]);
};
